import React, { useState, useRef, useCallback } from 'react';
import { 
  Input, 
  Button, 
  Typography, 
  Card,
  Tag,
  Tooltip
} from 'antd';
import { 
  SendOutlined, 
  PlusOutlined, 
  QuestionCircleOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { CommandRegistry } from '../../services/commands/CommandRegistry';
import { ThreadManager } from '../../services/threads/ThreadManager';
import { NewThreadDialog } from '../NewThreadDialog';
import { useNavigation } from '../../contexts/NavigationContext';
import { ModelType } from '../../types/models';

const { Text } = Typography;

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onCommand?: (command: string, args: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  showSuggestions?: boolean;
  showCommandHelp?: boolean;
}

interface CommandSuggestion {
  command: string;
  description: string;
  usage: string;
  category: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onCommand,
  placeholder = "Type a message or use / for commands...",
  disabled = false,
  showSuggestions = true,
  showCommandHelp = true,
}) => {
  const [input, setInput] = useState('');
  const [showCommandDropdown, setShowCommandDropdown] = useState(false);
  const [commandSuggestions, setCommandSuggestions] = useState<CommandSuggestion[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [showNewThreadDialog, setShowNewThreadDialog] = useState(false);
  
  const inputRef = useRef<any>(null);
  const commandRegistry = CommandRegistry.getInstance();
  const threadManager = ThreadManager.getInstance();
  const { createThreadFromContext } = useNavigation();

  // Handle input changes
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);

    // Check if user is typing a command
    if (value.startsWith('/')) {
      const commandPart = value.slice(1).toLowerCase();
      if (commandPart.length > 0) {
        const suggestions = commandRegistry.searchCommands(commandPart)
          .slice(0, 5)
          .map(cmd => ({
            command: cmd.name,
            description: cmd.description,
            usage: cmd.usage,
            category: cmd.category,
          }));
        
        setCommandSuggestions(suggestions);
        setShowCommandDropdown(suggestions.length > 0);
        setSelectedSuggestionIndex(0);
      } else {
        // Show all commands when just "/" is typed
        const allCommands = commandRegistry.getAllCommands()
          .slice(0, 10)
          .map(cmd => ({
            command: cmd.name,
            description: cmd.description,
            usage: cmd.usage,
            category: cmd.category,
          }));
        
        setCommandSuggestions(allCommands);
        setShowCommandDropdown(true);
        setSelectedSuggestionIndex(0);
      }
    } else {
      setShowCommandDropdown(false);
      setCommandSuggestions([]);
    }
  }, [commandRegistry]);

  // Handle key presses
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'ArrowDown' && showCommandDropdown) {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        Math.min(prev + 1, commandSuggestions.length - 1)
      );
    } else if (e.key === 'ArrowUp' && showCommandDropdown) {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Tab' && showCommandDropdown && commandSuggestions.length > 0) {
      e.preventDefault();
      const selectedSuggestion = commandSuggestions[selectedSuggestionIndex];
      if (selectedSuggestion) {
        setInput(`/${selectedSuggestion.command} `);
        setShowCommandDropdown(false);
        inputRef.current?.focus();
      }
    } else if (e.key === 'Escape') {
      setShowCommandDropdown(false);
    }
  }, [showCommandDropdown, commandSuggestions, selectedSuggestionIndex]);

  // Handle sending messages
  const handleSend = useCallback(() => {
    if (!input.trim() || disabled) return;

    const message = input.trim();
    setInput('');
    setShowCommandDropdown(false);
    setIsTyping(true);

    // Always send the user's message first (whether it's a command or regular message)
    onSendMessage(message);

    // Check if it's a command and execute it
    if (message.startsWith('/')) {
      const [command, ...args] = message.slice(1).split(' ');
      onCommand?.(command, args);
    }

    // Reset typing state after a short delay
    setTimeout(() => setIsTyping(false), 1000);
  }, [input, disabled, onSendMessage, onCommand]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((suggestion: CommandSuggestion) => {
    setInput(`/${suggestion.command} `);
    setShowCommandDropdown(false);
    inputRef.current?.focus();
  }, []);

  // Handle new thread creation
  const handleNewThread = (threadData: { name: string; model: any; uiRole: string; description?: string }) => {
    // Set environment based on model type
    const environment = threadData.model?.type === 'prime' ? 'Prime' : 'Company';
    
    createThreadFromContext(threadData.name, threadData.model, threadData.uiRole, {
      environment,
    });
    setShowNewThreadDialog(false);
  };

  // Quick actions
  const quickActions = [
    {
      label: 'New Thread',
      icon: <PlusOutlined />,
      action: () => {
        setShowNewThreadDialog(true);
      },
    },
    {
      label: 'Help',
      icon: <QuestionCircleOutlined />,
      action: () => {
        setInput('/help ');
        inputRef.current?.focus();
      },
    },
    {
      label: 'AI Assistant',
      icon: <ThunderboltOutlined />,
      action: () => {
        setInput('/ask ');
        inputRef.current?.focus();
      },
    },
  ];

  // Command help overlay
  const renderCommandHelp = () => {
    if (!showCommandHelp || !showCommandDropdown) return null;

    return (
      <Card
        size="small"
        style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 1000,
          marginTop: 4,
          maxHeight: 300,
          overflow: 'auto',
        }}
      >
        <div style={{ marginBottom: 8 }}>
          <Text strong>Available Commands</Text>
        </div>
        
        {commandSuggestions.map((suggestion, index) => (
          <div
            key={suggestion.command}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              backgroundColor: index === selectedSuggestionIndex ? '#f0f9ff' : 'transparent',
              borderRadius: 4,
              marginBottom: 4,
            }}
            onClick={() => handleSuggestionSelect(suggestion)}
            onMouseEnter={() => setSelectedSuggestionIndex(index)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text code style={{ fontSize: 12 }}>
                /{suggestion.command}
              </Text>
              <Tag color="blue">
                {suggestion.category}
              </Tag>
            </div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
              {suggestion.description}
            </div>
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {suggestion.usage}
            </div>
          </div>
        ))}
        
        {commandSuggestions.length === 0 && (
          <div style={{ padding: 8, textAlign: 'center', color: '#999' }}>
            No commands found
          </div>
        )}
      </Card>
    );
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Quick Actions */}
      <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
        {quickActions.map((action, index) => (
          <Tooltip key={index} title={action.label}>
            <Button
              size="small"
              icon={action.icon}
              onClick={action.action}
              disabled={disabled}
            >
              {action.label}
            </Button>
          </Tooltip>
        ))}
      </div>

      {/* Input Area */}
      <div style={{ position: 'relative' }}>
        <Input
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          suffix={
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              disabled={!input.trim() || disabled}
              loading={isTyping}
              size="small"
            />
          }
          style={{
            paddingRight: 40,
          }}
        />

        {/* Command Suggestions Dropdown */}
        {renderCommandHelp()}
      </div>

      {/* Command Categories Help */}
      {showSuggestions && !showCommandDropdown && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Try: <Text code>/help</Text> for commands, <Text code>/thread create</Text> for new threads, <Text code>/pin add</Text> to pin artifacts
          </Text>
        </div>
      )}

      {/* New Thread Dialog */}
      <NewThreadDialog
        visible={showNewThreadDialog}
        onCancel={() => setShowNewThreadDialog(false)}
        onOk={handleNewThread}
      />
    </div>
  );
};
